import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { TrendingUp, DollarSign, Package, BarChart3, Percent, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function ReportsPage() {
    const { t, language } = useLanguage();
    const [salesData, setSalesData] = useState(null);
    const [topProducts, setTopProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('30');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchReports();
    }, [dateRange]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const days = parseInt(dateRange);
            const endDate = new Date().toISOString();
            const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

            const [salesRes, topRes] = await Promise.all([
                axios.get(`${API_URL}/reports/sales?startDate=${startDate}&endDate=${endDate}`, { headers }),
                axios.get(`${API_URL}/reports/top-products?startDate=${startDate}&endDate=${endDate}&limit=10`, { headers })
            ]);

            setSalesData(salesRes.data.data);
            setTopProducts(topRes.data.data.products || []);
        } catch (error) {
            console.error('Failed to fetch reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return `${parseFloat(value || 0).toFixed(2)} TND`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' });
    };

    // Calculate max revenue for chart scaling
    const maxRevenue = useMemo(() => {
        if (!salesData?.dailySales?.length) return 0;
        return Math.max(...salesData.dailySales.map(d => d.revenue || 0), 1);
    }, [salesData]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    const summary = salesData?.summary || {};
    const dailySales = salesData?.dailySales || [];

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('reportsTitle')}</h1>
                        <p className="text-muted-foreground">{t('reportsSubtitle')}</p>
                    </div>
                    <Select value={dateRange} onValueChange={setDateRange}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">{t('last7days')}</SelectItem>
                            <SelectItem value="30">{t('last30days')}</SelectItem>
                            <SelectItem value="90">{t('last90days')}</SelectItem>
                            <SelectItem value="365">{t('lastYear')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('totalSales')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold flex items-center">
                                <DollarSign className="h-4 w-4 mr-1 text-muted-foreground" />
                                {parseFloat(summary.totalRevenue || 0).toFixed(2)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center">
                                <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
                                {t('revenueGenerated')}
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('ordersPlaced')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold flex items-center">
                                <ShoppingCart className="h-4 w-4 mr-1 text-muted-foreground" />
                                {summary.orderCount || 0}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center">
                                <BarChart3 className="h-3 w-3 mr-1 text-blue-500" />
                                {t('volume')}
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('avgOrderValue')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold flex items-center">
                                <DollarSign className="h-4 w-4 mr-1 text-muted-foreground" />
                                {parseFloat(summary.averageOrderValue || 0).toFixed(2)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 text-muted-foreground">
                                {t('revenue')} / {t('orders')}
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('netProfit')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className={`text-2xl font-bold flex items-center ${(summary.grossProfit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                <DollarSign className="h-4 w-4 mr-1" />
                                {parseFloat(summary.grossProfit || 0).toFixed(2)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center">
                                <Percent className="h-3 w-3 mr-1 text-green-500" />
                                {summary.profitMargin || 0}% {t('profitability')}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Sales Chart & Top Products */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('salesTrend')}</CardTitle>
                            <CardDescription>{t('revenue')}</CardDescription>
                        </CardHeader>
                        <CardContent className="h-[300px] border-t pt-4">
                            {dailySales.length === 0 ? (
                                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                                    <Package className="h-8 w-8 mr-2 opacity-50" />
                                    {t('noDataAvailable')}
                                </div>
                            ) : (
                                <div className="h-full flex flex-col">
                                    {/* Bar Chart */}
                                    <div className="flex-1 flex items-end gap-1 px-2">
                                        {dailySales.map((day, index) => {
                                            const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                                            return (
                                                <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                                                    {/* Tooltip */}
                                                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover text-popover-foreground text-xs rounded px-2 py-1 whitespace-nowrap z-10 shadow-lg border">
                                                        <div className="font-medium">{formatDate(day.date)}</div>
                                                        <div>{formatCurrency(day.revenue)}</div>
                                                        <div className="text-muted-foreground">{day.units} {t('units')}</div>
                                                    </div>
                                                    {/* Bar */}
                                                    <div
                                                        className="w-full bg-primary/80 hover:bg-primary rounded-t transition-all duration-200"
                                                        style={{ height: `${Math.max(height, 2)}%` }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* X-axis labels */}
                                    <div className="flex gap-1 px-2 mt-2 text-xs text-muted-foreground overflow-hidden">
                                        {dailySales.length <= 15 ? (
                                            dailySales.map((day) => (
                                                <div key={day.date} className="flex-1 text-center truncate">
                                                    {formatDate(day.date)}
                                                </div>
                                            ))
                                        ) : (
                                            <>
                                                <div className="flex-1 text-left">{formatDate(dailySales[0]?.date)}</div>
                                                <div className="flex-1 text-center">{formatDate(dailySales[Math.floor(dailySales.length / 2)]?.date)}</div>
                                                <div className="flex-1 text-right">{formatDate(dailySales[dailySales.length - 1]?.date)}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>{t('topProducts')}</CardTitle>
                            <CardDescription>{t('byRevenue')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {topProducts.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                                    <Package className="h-8 w-8 mr-2 opacity-50" />
                                    {t('noDataAvailable')}
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[80px]">{t('rank')}</TableHead>
                                            <TableHead>{t('product')}</TableHead>
                                            <TableHead className="text-right">{t('soldInfo')}</TableHead>
                                            <TableHead className="text-right">{t('revenue')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {topProducts.map((product, index) => (
                                            <TableRow key={product.productId}>
                                                <TableCell className="font-medium text-muted-foreground">
                                                    #{index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="font-medium">{product.productName}</div>
                                                    <div className="text-xs text-muted-foreground">{product.sku}</div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {product.unitsSold}
                                                </TableCell>
                                                <TableCell className="text-right font-medium">
                                                    {formatCurrency(product.revenue)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
